// frontend/src/features/ups/ManageInternalDrivers.js
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Eye, Trash2, UserCheck } from 'lucide-react';

import {
  fetchInternalDrivers,
  createInternalDriver,
  updateInternalDriver,
  deleteInternalDriver,
  reserveDriver,
  activateDriver,
  unbanDriver,
} from '../../services/upsDriverService';

import InternalDriverFormModal from './modals/InternalDriverFormModal';
import DriverDetailsModal from './modals/DriverDetailsModal';
import DriverMobileCard from './components/DriverMobileCard';
import { COMPANY_ACTION_CLASSES, CompanyStatusBadge } from './ui';

import {
  selectCompanySites,
  loadMyCompanySites,
  selectSitesLoading,
} from '../sites/sitesSlice';
import { upsertDriver } from '../drivers/driverSlice';

const RESERVE_ICON_ACTION_CLASS =
  'inline-flex h-10 w-10 items-center justify-center rounded-xl border border-app-border bg-app-card font-semibold text-app-accent transition hover:bg-app-surface focus:outline-none focus:ring-2 focus:ring-app-accent/30 disabled:cursor-not-allowed disabled:opacity-60';

const ACTIVATE_ICON_ACTION_CLASS =
  'inline-flex h-10 w-10 items-center justify-center rounded-xl border border-app-border bg-app-card font-semibold text-green-700 transition hover:bg-app-surface focus:outline-none focus:ring-2 focus:ring-green-200 disabled:cursor-not-allowed disabled:opacity-60';

function SourceBadge({ workforceSource }) {
  const source = (workforceSource || 'COMPANY').toString().toUpperCase();

  return (
    <span className="inline-flex items-center rounded-full border border-app-border bg-app-surface px-3 py-1 text-xs font-semibold text-app-muted">
      {source === 'COMPANY' ? 'Internal' : source}
    </span>
  );
}

function StateBadge({ state }) {
  const st = String(state || 'available').toLowerCase();

  const toneByState = {
    active: 'active',
    reserved: 'warning',
    requested: 'requested',
    banned: 'banned',
  };

  return (
    <CompanyStatusBadge tone={toneByState[st] || 'neutral'}>
      {formatStateLabel(st)}
    </CompanyStatusBadge>
  );
}

function resolveInternalDriverState(row) {
  if (row.current_state) return String(row.current_state).toLowerCase();

  if (row.is_banned) return 'banned';

  const status = String(row.assignment_status || '').toLowerCase();

  if (status === 'active') return 'active';
  if (status === 'confirmed') return 'reserved';
  if (status === 'requested') return 'requested';

  return 'available';
}

function formatStateLabel(state) {
  switch (state) {
    case 'banned':
      return 'Banned';
    case 'active':
      return 'Active';
    case 'reserved':
      return 'Reserved';
    case 'requested':
      return 'Requested';
    default:
      return 'Available';
  }
}

function getCurrentSiteLabel(row) {
  if (row.site_name) return row.site_name;
  if (row.site_id) return `Site #${row.site_id}`;
  return '—';
}

function getPrimaryAction(row) {
  const state = resolveInternalDriverState(row);

  switch (state) {
    case 'available':
    case 'active':
      return {
        label: 'Reserve',
        action: 'reserve',
        className: COMPANY_ACTION_CLASSES.compactWarning,
      };

    case 'reserved':
      return {
        label: 'Activate',
        action: 'activate',
        className: COMPANY_ACTION_CLASSES.compactInfo,
      };

    case 'banned':
      return {
        label: 'Unban',
        action: 'unban',
        className: COMPANY_ACTION_CLASSES.compactSuccess,
      };

    default:
      return null;
  }
}

function getPrimaryActionIconClassName(primaryAction) {
  if (primaryAction.action === 'reserve') return RESERVE_ICON_ACTION_CLASS;
  return ACTIVATE_ICON_ACTION_CLASS;
}

function mergeDetailsDriver(prev, updated, payload) {
  if (!prev) return prev;

  const operationalContext = {
    assignment_id: prev.assignment_id,
    assignment_status: prev.assignment_status,
    starts_at: prev.starts_at,
    start_time: prev.start_time,
    ends_at: prev.ends_at,
    end_time: prev.end_time,
    site_id: prev.site_id,
    site_name: prev.site_name,
    current_state: prev.current_state,
    is_banned: prev.is_banned,
    ban_id: prev.ban_id,
    ban_reason: prev.ban_reason,
    banned_at: prev.banned_at,
    blocked_reason: prev.blocked_reason,
    reason: prev.reason,
  };

  return {
    ...prev,
    ...(updated || {}),
    ...payload,
    vehicle_registration_number:
      payload.vehicle_registration_number ??
      updated?.vehicle_registration_number ??
      prev.vehicle_registration_number,
    vrn:
      payload.vehicle_registration_number ??
      updated?.vrn ??
      prev.vrn,
    ...operationalContext,
  };
}

export default function ManageInternalDrivers() {
  const dispatch = useDispatch();

  const sites = useSelector(selectCompanySites);
  const sitesLoading = useSelector(selectSitesLoading);

  const [siteFilter, setSiteFilter] = useState('all');
  const [dispatchSiteId, setDispatchSiteId] = useState('');

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [query, setQuery] = useState('');

  const [showForm, setShowForm] = useState(false);
  const [editingDriver, setEditingDriver] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const [stateFilter, setStateFilter] = useState('all');
  const [detailsDriver, setDetailsDriver] = useState(null);
  const [actionBusyId, setActionBusyId] = useState(null);

  const isAliveRef = useRef(true);

  const load = useCallback(async () => {
    if (!isAliveRef.current) return;

    setLoading(true);
    setError('');

    try {
      const data = await fetchInternalDrivers();
      if (!isAliveRef.current) return;
      setRows(Array.isArray(data) ? data : []);
    } catch (err) {
      if (!isAliveRef.current) return;
      setError(
        err?.response?.data?.error ||
          err?.message ||
          'Failed to load internal drivers'
      );
    } finally {
      if (!isAliveRef.current) return;
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    isAliveRef.current = true;
    return () => {
      isAliveRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (sites.length > 0 || sitesLoading) return;
    dispatch(loadMyCompanySites());
  }, [dispatch, sites.length, sitesLoading]);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();

    return rows.filter((row) => {
      const resolvedState = resolveInternalDriverState(row);

      const matchesState = stateFilter === 'all' ? true : resolvedState === stateFilter;

      const matchesSite =
        !siteFilter || String(siteFilter) === 'all'
          ? true
          : String(row.site_id) === String(siteFilter);

      const matchesSearch =
        !q ||
        (row.name || '').toLowerCase().includes(q) ||
        (row.email || '').toLowerCase().includes(q) ||
        (row.phone || '').toLowerCase().includes(q) ||
        (row.vehicle_registration_number || '').toLowerCase().includes(q);

      return matchesState && matchesSite && matchesSearch;
    });
  }, [rows, query, stateFilter, siteFilter]);

  const openDetails = (driver) => {
    setDetailsDriver(driver);
  };

  const handleSiteFilterChange = (e) => {
    setSiteFilter(e.target.value);
  };

  const handleDispatchSiteChange = (e) => {
    setDispatchSiteId(e.target.value);
  };

  const handlePrimaryAction = async (driver) => {
    const state = resolveInternalDriverState(driver);
    setError('');
    setActionBusyId(driver.id);

    try {
      if (state === 'banned') {
        await unbanDriver(driver.ban_id || driver.id);
        await load();
        return;
      }

      if (!dispatchSiteId) {
        setError('Select a dispatch site first.');
        return;
      }

      if (state === 'available' || state === 'active') {
        await reserveDriver(driver.id, dispatchSiteId);
        await load();
        return;
      }

      if (state === 'reserved') {
        await activateDriver(driver.id, dispatchSiteId);
        await load();
      }
    } catch (err) {
      setError(
        err?.response?.data?.error ||
          err?.message ||
          'Failed to perform driver action'
      );
    } finally {
      setActionBusyId(null);
    }
  };

  const openCreate = () => {
    setEditingDriver(null);
    setShowForm(true);
  };

  const closeForm = () => {
    if (submitting) return;
    setShowForm(false);
    setEditingDriver(null);
  };

  const handleSubmit = async (formValues) => {
    setSubmitting(true);
    setError('');

    try {
      if (editingDriver?.id) {
        await updateInternalDriver(editingDriver.id, formValues);
      } else {
        await createInternalDriver(formValues);
      }

      setShowForm(false);
      setEditingDriver(null);
      await load();
    } catch (err) {
      setError(
        err?.response?.data?.error ||
          err?.message ||
          'Failed to save internal driver'
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleSaveDetails = async (driverId, payload) => {
    setError('');

    const updated = await updateInternalDriver(driverId, payload);
    if (updated) dispatch(upsertDriver(updated));

    setDetailsDriver((prev) => {
      if (!prev || String(prev.id) !== String(driverId)) return prev;
      return mergeDetailsDriver(prev, updated, payload);
    });
    await load();

    return updated;
  };

  const handleDelete = async (driver) => {
    const label = driver?.name || `Driver #${driver?.id ?? ''}`;
    const ok = window.confirm(`Delete internal driver "${label}"?`);
    if (!ok) return;

    setError('');

    try {
      await deleteInternalDriver(driver.id);
      await load();
    } catch (err) {
      setError(
        err?.response?.data?.error ||
          err?.message ||
          'Failed to delete internal driver'
      );
    }
  };

  const totalCount = rows.length;
  const filteredCount = filtered.length;
  const filtersActive =
    !!query.trim() ||
    stateFilter !== 'all' ||
    String(siteFilter || 'all') !== 'all';

  return (
    <>
    <div className="space-y-6 p-6">
      {/* Page header */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.22em] text-app-accent">
            Administration
          </p>
          <h1 className="mt-2 text-2xl font-bold text-app-text">Internal Drivers</h1>
          <p className="mt-1 text-sm text-app-muted">
            Company-owned driver roster and dispatch control for UPS/company admins.
          </p>
        </div>

        <div className="flex flex-wrap gap-2">
          <button
            onClick={openCreate}
            className="rounded-xl bg-app-primary px-5 py-2.5 text-sm font-bold text-white shadow-sm transition hover:opacity-90"
          >
            New internal driver
          </button>

          <button
            onClick={load}
            disabled={loading}
            className="rounded-xl border border-app-border bg-app-card px-4 py-2.5 text-sm font-semibold text-app-text transition hover:bg-app-surface disabled:cursor-not-allowed disabled:opacity-60"
          >
            {loading ? 'Refreshing…' : 'Refresh'}
          </button>
        </div>
      </div>

      {/* Filters and dispatch target */}
      <section className="rounded-2xl border border-app-border bg-app-card p-5 shadow-sm">
        <div className="grid gap-4 xl:grid-cols-[1fr_1fr_1fr_1.5fr]">
          <div>
            <label
              htmlFor="internal-current-site-filter"
              className="text-xs font-semibold uppercase tracking-[0.18em] text-app-muted"
            >
              Current Site
            </label>
            <select
              id="internal-current-site-filter"
              name="internalCurrentSiteFilter"
              className="mt-2 w-full rounded-xl border border-app-border bg-app-card px-4 py-3 text-sm text-app-text outline-none transition focus:border-app-primary focus:ring-2 focus:ring-app-primary/15"
              value={siteFilter}
              onChange={handleSiteFilterChange}
            >
              <option value="all">All Sites</option>
              {sites.map((site) => (
                <option key={site.id} value={site.id}>
                  {site.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label
              htmlFor="internal-dispatch-site"
              className="text-xs font-semibold uppercase tracking-[0.18em] text-app-muted"
            >
              Dispatch Site
            </label>
            <select
              id="internal-dispatch-site"
              name="internalDispatchSite"
              className="mt-2 w-full rounded-xl border border-app-primary/40 bg-app-card px-4 py-3 text-sm text-app-text outline-none transition focus:border-app-primary focus:ring-2 focus:ring-app-primary/15"
              value={dispatchSiteId}
              onChange={handleDispatchSiteChange}
            >
              <option value="">Select site…</option>
              {sites.map((site) => (
                <option key={site.id} value={site.id}>
                  {site.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label
              htmlFor="internal-state-filter"
              className="text-xs font-semibold uppercase tracking-[0.18em] text-app-muted"
            >
              State
            </label>
            <select
              id="internal-state-filter"
              name="internalStateFilter"
              className="mt-2 w-full rounded-xl border border-app-border bg-app-card px-4 py-3 text-sm text-app-text outline-none transition focus:border-app-primary focus:ring-2 focus:ring-app-primary/15"
              value={stateFilter}
              onChange={(e) => setStateFilter(e.target.value)}
            >
              <option value="all">All States</option>
              <option value="available">Available</option>
              <option value="reserved">Reserved</option>
              <option value="active">Active</option>
              <option value="requested">Requested</option>
              <option value="banned">Banned</option>
            </select>
          </div>

          <div>
            <label
              htmlFor="internal-driver-search"
              className="text-xs font-semibold uppercase tracking-[0.18em] text-app-muted"
            >
              Search
            </label>
            <input
              id="internal-driver-search"
              name="internalDriverSearch"
              type="text"
              className="mt-2 w-full rounded-xl border border-app-border bg-app-card px-4 py-3 text-sm text-app-text outline-none transition placeholder:text-app-muted focus:border-app-primary focus:ring-2 focus:ring-app-primary/15"
              placeholder="Search name, email, phone, or vehicle…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>
        </div>

        {error && (
          <div className="mt-4 rounded-xl border border-red-200 bg-red-50 p-3 text-sm font-medium text-red-700">
            {error}
          </div>
        )}
      </section>

      {/* Summary */}
      <section className="rounded-2xl border border-app-border bg-app-card p-5 shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="text-sm text-app-muted">
            Showing <span className="font-bold text-app-text">{filteredCount}</span>
            <span className="mx-1">of</span>
            <span className="font-bold text-app-text">{totalCount}</span>
            <span> internal drivers</span>

            {filtersActive && (
              <span className="ml-2 rounded-full bg-app-surface px-3 py-1 text-xs font-semibold text-app-muted">
                Filters active
              </span>
            )}
          </div>
        </div>
      </section>

      {/* Internal drivers table */}
      <section className="overflow-hidden rounded-2xl border border-app-border bg-app-card shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-4 border-b border-app-border px-6 py-5">
          <div>
            <h2 className="text-xl font-bold text-app-text">Drivers</h2>
            <p className="mt-1 text-sm text-app-muted">
              Manage internal availability, dispatch state, and driver records.
            </p>
          </div>

          <div className="rounded-full bg-app-surface px-4 py-2 text-sm font-semibold text-app-muted">
            {filteredCount} shown
          </div>
        </div>

        <div className="hidden overflow-x-auto xl:block">
          <table className="min-w-full bg-app-card text-sm">
            <thead className="border-b border-app-border bg-app-surface">
              <tr>
                {['Driver', 'Vehicle', 'Contact', 'Current State', 'Current Site', 'Source', 'Actions'].map(
                  (col) => (
                    <th
                      key={col}
                      className={`px-6 py-4 text-xs font-semibold uppercase tracking-[0.18em] text-app-muted ${
                        col === 'Actions' ? 'text-right' : 'text-left'
                      }`}
                    >
                      {col}
                    </th>
                  )
                )}
              </tr>
            </thead>

            <tbody className="divide-y divide-app-border">
              {loading ? (
                <tr>
                  <td colSpan={7} className="px-6 py-12 text-center text-sm text-app-muted">
                    Loading internal drivers…
                  </td>
                </tr>
              ) : filtered.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-6 py-12 text-center text-sm text-app-muted">
                    <div>
                      {filtersActive
                        ? 'No internal drivers match the current filters.'
                        : 'No internal drivers yet. Create your first internal driver.'}
                    </div>

                    <button
                      onClick={openCreate}
                      className="mt-4 rounded-xl bg-app-primary px-5 py-2.5 text-sm font-bold text-white shadow-sm transition hover:opacity-90"
                    >
                      New internal driver
                    </button>
                  </td>
                </tr>
              ) : (
                filtered.map((driver) => {
                  const resolvedState = resolveInternalDriverState(driver);
                  const currentSiteLabel = getCurrentSiteLabel(driver);
                  const primaryAction = getPrimaryAction(driver);
                  const primaryActionIconClassName = primaryAction
                    ? getPrimaryActionIconClassName(primaryAction)
                    : '';
                  const isActionBusy = actionBusyId === driver.id;

                  return (
                    <tr key={driver.id} className="transition hover:bg-app-surface">
                      <td className="px-6 py-5">
                        <div className="font-semibold text-app-text">{driver.name || '—'}</div>
                        <div className="mt-1 text-xs text-app-muted">
                          {driver.submission_date
                            ? `Created · ${new Date(driver.submission_date).toLocaleString()}`
                            : 'Created · —'}
                        </div>
                      </td>

                      <td className="whitespace-nowrap px-6 py-5 font-mono text-xs text-app-text">
                        {driver.vehicle_registration_number || '—'}
                      </td>

                      <td className="px-6 py-5 text-sm text-app-text">
                        <div>{driver.phone || '—'}</div>
                        <div className="mt-1 text-xs text-app-muted">{driver.email || '—'}</div>
                      </td>

                      <td className="whitespace-nowrap px-6 py-5">
                        <StateBadge state={resolvedState} />
                      </td>

                      <td className="whitespace-nowrap px-6 py-5 text-sm text-app-text">
                        {currentSiteLabel}
                      </td>

                      <td className="whitespace-nowrap px-6 py-5">
                        <SourceBadge workforceSource={driver.workforce_source} />
                      </td>

                      <td className="px-6 py-5">
                        <div className="flex flex-wrap items-center justify-end gap-2">
                          <button
                            onClick={() => openDetails(driver)}
                            className={COMPANY_ACTION_CLASSES.iconSecondary}
                            aria-label="View details"
                            title="View details"
                          >
                            <Eye size={16} />
                          </button>

                          {primaryAction && (
                            <button
                              onClick={() => handlePrimaryAction(driver)}
                              disabled={isActionBusy}
                              className={primaryActionIconClassName}
                              aria-label={`${primaryAction.label} driver`}
                              title={`${primaryAction.label} driver`}
                            >
                              <UserCheck size={16} />
                            </button>
                          )}

                          <button
                            onClick={() => handleDelete(driver)}
                            className={COMPANY_ACTION_CLASSES.iconDestructive}
                            aria-label="Delete driver"
                            title="Delete driver"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        <div className="space-y-3 p-4 xl:hidden">
          {loading ? (
            <div className="rounded-2xl border border-app-border bg-app-surface px-4 py-10 text-center text-sm text-app-muted">
              Loading internal drivers...
            </div>
          ) : filtered.length === 0 ? (
            <div className="rounded-2xl border border-app-border bg-app-surface px-4 py-10 text-center text-sm text-app-muted">
              <div>
                {filtersActive
                  ? 'No internal drivers match the current filters.'
                  : 'No internal drivers yet. Create your first internal driver.'}
              </div>

              <button
                onClick={openCreate}
                className="mt-4 rounded-xl bg-app-primary px-5 py-2.5 text-sm font-bold text-white shadow-sm transition hover:opacity-90"
              >
                New internal driver
              </button>
            </div>
          ) : (
            filtered.map((driver) => {
              const resolvedState = resolveInternalDriverState(driver);
              const currentSiteLabel = getCurrentSiteLabel(driver);
              const primaryAction = getPrimaryAction(driver);
              const primaryActionIconClassName = primaryAction
                ? getPrimaryActionIconClassName(primaryAction)
                : '';
              const isActionBusy = actionBusyId === driver.id;
              const avatarText = (driver.name || '')
                .split(' ')
                .map((part) => part[0])
                .join('')
                .slice(0, 2);

              return (
                <DriverMobileCard
                  key={driver.id}
                  tone="internal"
                  title={driver.name || '—'}
                  subtitle={`Driver ID · ${driver.id ? `DRV-${driver.id}` : '—'}`}
                  avatarText={avatarText}
                  fields={[
                    ['Vehicle', driver.vehicle_registration_number],
                    ['Current State', formatStateLabel(resolvedState)],
                    ['Current Site', currentSiteLabel],
                    ['Phone', driver.phone],
                    ['Email', driver.email],
                    [
                      'Created',
                      driver.submission_date
                        ? new Date(driver.submission_date).toLocaleString()
                        : null,
                    ],
                  ]}
                  badges={[
                    <StateBadge key="state" state={resolvedState} />,
                    <SourceBadge key="source" workforceSource={driver.workforce_source} />,
                    driver.is_banned && resolvedState !== 'banned' ? (
                      <CompanyStatusBadge key="banned" tone="banned">
                        Banned
                      </CompanyStatusBadge>
                    ) : null,
                  ]}
                  actions={
                    <>
                      <button
                        onClick={() => openDetails(driver)}
                        className={COMPANY_ACTION_CLASSES.iconSecondary}
                        aria-label="View details"
                        title="View details"
                      >
                        <Eye size={16} />
                      </button>

                      {primaryAction && (
                        <button
                          onClick={() => handlePrimaryAction(driver)}
                          disabled={isActionBusy}
                          className={primaryActionIconClassName}
                          aria-label={`${primaryAction.label} driver`}
                          title={`${primaryAction.label} driver`}
                        >
                          <UserCheck size={16} />
                        </button>
                      )}

                      <button
                        onClick={() => handleDelete(driver)}
                        className={COMPANY_ACTION_CLASSES.iconDestructive}
                        aria-label="Delete driver"
                        title="Delete driver"
                      >
                        <Trash2 size={16} />
                      </button>
                    </>
                  }
                />
              );
            })
          )}
        </div>
      </section>

    </div>

    {showForm && (
      <InternalDriverFormModal
        isOpen={showForm}
        onClose={closeForm}
        onSubmit={handleSubmit}
        driver={editingDriver}
        submitting={submitting}
      />
    )}

    {detailsDriver && (
      <DriverDetailsModal
        driver={detailsDriver}
        onClose={() => setDetailsDriver(null)}
        onSaveDetails={handleSaveDetails}
        onMoveSuccess={load}
        refreshAssignmentsAfterMove={false}
      />
    )}
    </>
  );
}
